import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bot } from 'lucide-react';
import type { RunningAgent } from '@/types';
import { routes } from '@/lib/routes';
import { formatRelativeTime } from '@/pages/activity/utils';
import { useMeAgents as useAgents } from '@/hooks/useAgents';
import { OverviewPanel } from './OverviewPanel';

interface RunningAgentsPanelProps {
  runs?: RunningAgent[];
  isLoading?: boolean;
}

export function RunningAgentsPanel({ runs, isLoading }: RunningAgentsPanelProps) {
  const list = runs ?? [];
  const { data: agentsData } = useAgents();
  const agentNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agentsData?.items ?? []) {
      map.set(agent.agentId, agent.name);
    }
    return map;
  }, [agentsData]);

  return (
    <OverviewPanel
      title="Running Agents"
      icon={<Bot className="h-5 w-5" />}
      isLoading={isLoading && !runs}
      isEmpty={!isLoading && list.length === 0}
      emptyMessage="No active agents"
      bodyClassName="p-0 min-h-24"
    >
      <ul className="divide-y divide-border">
        {list.map((run) => (
          <li key={run.runId} className="hover:bg-accent/40 transition-colors">
            <Link
              to={routes.console.run(run.runId)}
              className="flex items-center gap-3 px-4 py-2.5"
            >
              <span className="relative flex h-2 w-2 shrink-0" aria-label="running">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
              </span>
              <p className="text-sm text-foreground truncate flex-1">
                {run.agentName ||
                  (run.agentId ? agentNameById.get(run.agentId) : undefined) ||
                  run.agentId ||
                  'Agent'}
              </p>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatRelativeTime(run.startedAt)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </OverviewPanel>
  );
}
